import { useState } from 'react';

import { api } from '../api';
import type { PortalConfig } from '../config';
import type { AppInput, OnboardedApp, Viewer } from '../types';
import { AppStatusBadge, AsyncBoundary, Notice, formatDate, useAsync } from '../ui';

export function AppsView({ config, viewer }: { config: PortalConfig; viewer: Viewer }) {
  const state = useAsync(() => api.apps(config), [config]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<OnboardedApp | undefined>();
  const [removing, setRemoving] = useState<OnboardedApp | undefined>();
  const [message, setMessage] = useState<string | undefined>();

  function done(text: string) {
    setMessage(text);
    setAdding(false);
    setEditing(undefined);
    setRemoving(undefined);
    state.reload();
  }

  return (
    <>
      <p className="muted">
        Onboarding an app sets up two AWS Security Agent configurations: one testing what an anonymous visitor can reach,
        and one signed in as the shared Lighthouse pen test account. Targets must sit under{' '}
        <code>{viewer.sandboxParentDomain}</code>, the domain verified for pen testing.
      </p>

      {!viewer.isAdmin ? (
        <Notice kind="warn">
          You have read-only access. Onboarding and removing apps needs the <code>{viewer.adminPermission}</code>{' '}
          permission on your user in the GTM tools permissions table.
        </Notice>
      ) : null}

      {message ? <Notice>{message}</Notice> : null}

      {viewer.isAdmin && !adding && !editing ? (
        <div className="section">
          <button className="action" onClick={() => setAdding(true)}>
            Onboard an app
          </button>
        </div>
      ) : null}

      {adding ? (
        <div className="section">
          <AppForm
            config={config}
            viewer={viewer}
            onCancel={() => setAdding(false)}
            onDone={(app) => done(`Onboarded ${app.displayName}. Its two pen test configurations are ready to run.`)}
          />
        </div>
      ) : null}

      {editing ? (
        <div className="section">
          <AppForm
            config={config}
            viewer={viewer}
            existing={editing}
            onCancel={() => setEditing(undefined)}
            onDone={(app) => done(`Updated the pen test scope for ${app.displayName}.`)}
          />
        </div>
      ) : null}

      <div className="section">
        <AsyncBoundary state={state}>
          {({ apps }) =>
            apps.length === 0 ? (
              <Notice kind="warn">
                No apps onboarded yet. Add one to start scanning its code and pen testing its sandbox environment.
              </Notice>
            ) : (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Application</th>
                      <th>Repository</th>
                      <th>Target</th>
                      <th>Status</th>
                      <th>Added</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((app) => (
                      <tr key={app.slug}>
                        <td>
                          <div className="finding-title">{app.displayName}</div>
                          <code>{app.slug}</code>
                        </td>
                        <td>
                          <a
                            href={`https://github.com/${app.repository}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {app.repository}
                          </a>
                        </td>
                        <td>
                          <a href={app.sandboxUrl} target="_blank" rel="noreferrer">
                            {app.sandboxUrl.replace(/^https:\/\//, '')}
                          </a>
                        </td>
                        <td>
                          <AppStatusBadge value={app.status} />
                          {app.status === 'FAILED' && app.statusReason ? (
                            <div className="muted">{app.statusReason}</div>
                          ) : null}
                        </td>
                        <td className="muted">
                          {formatDate(app.createdAt)}
                          <div>{app.createdBy}</div>
                        </td>
                        <td>
                          {viewer.isAdmin ? (
                            <div className="row-actions">
                              <button
                                className="action secondary"
                                onClick={() => setEditing(app)}
                                disabled={app.status !== 'ACTIVE'}
                              >
                                Edit scope
                              </button>
                              <button className="action secondary" onClick={() => setRemoving(app)}>
                                Remove
                              </button>
                            </div>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </AsyncBoundary>
      </div>

      {removing ? (
        <RemoveAppDialog
          config={config}
          app={removing}
          onCancel={() => setRemoving(undefined)}
          onDone={() => done(`Removed ${removing.displayName} and everything created for it.`)}
        />
      ) : null}
    </>
  );
}

/** Newline-separated textareas are easier to paste into than comma lists. */
const toList = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

function AppForm({
  config,
  viewer,
  existing,
  onCancel,
  onDone,
}: {
  config: PortalConfig;
  viewer: Viewer;
  existing?: OnboardedApp;
  onCancel: () => void;
  onDone: (app: OnboardedApp) => void;
}) {
  const [slug, setSlug] = useState(existing?.slug ?? '');
  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [repository, setRepository] = useState(existing?.repository ?? `${viewer.githubOrg}/`);
  const [sandboxUrl, setSandboxUrl] = useState(existing?.sandboxUrl ?? '');
  const [apiUrls, setApiUrls] = useState((existing?.apiUrls ?? []).join('\n'));
  const [loginUrl, setLoginUrl] = useState(existing?.loginUrl ?? '');
  const [loginPrompt, setLoginPrompt] = useState(existing?.loginPrompt ?? '');
  const [accessibleUrls, setAccessibleUrls] = useState((existing?.accessibleUrls ?? []).join('\n'));
  const [outOfScopeUrls, setOutOfScopeUrls] = useState((existing?.outOfScopeUrls ?? []).join('\n'));
  const [excludeDos, setExcludeDos] = useState(
    existing ? existing.excludeRiskTypes.includes('DENIAL_OF_SERVICE') : true,
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    const input: AppInput = {
      slug: slug.trim(),
      displayName: displayName.trim(),
      repository: repository.trim(),
      sandboxUrl: sandboxUrl.trim(),
      apiUrls: toList(apiUrls),
      loginUrl: loginUrl.trim() || undefined,
      loginPrompt: loginPrompt.trim() || undefined,
      accessibleUrls: toList(accessibleUrls),
      outOfScopeUrls: toList(outOfScopeUrls),
      excludeRiskTypes: excludeDos ? ['DENIAL_OF_SERVICE'] : [],
    };

    try {
      onDone(existing ? await api.updateApp(config, input) : await api.onboardApp(config, input));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not save the app.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card app-form" onSubmit={submit}>
      <h2>{existing ? `Edit ${existing.displayName}` : 'Onboard an app'}</h2>

      {error ? <Notice kind="error">{error}</Notice> : null}

      <label>
        Display name
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={100} />
      </label>

      <label>
        Slug
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          required
          // Fixed after creation: it names the pen tests and their log streams.
          disabled={Boolean(existing)}
          pattern="[a-z0-9][a-z0-9-]{1,30}"
          placeholder="widget-portal"
        />
        <small>
          Lowercase letters, digits and hyphens. Used in resource names, so it cannot be changed after onboarding.
        </small>
      </label>

      <label>
        Repository
        <input value={repository} onChange={(e) => setRepository(e.target.value)} required placeholder="owner/name" />
        <small>Joins code scanning results to this app. Must be in {viewer.githubOrg}.</small>
      </label>

      <label>
        Sandbox URL
        <input
          type="url"
          value={sandboxUrl}
          onChange={(e) => setSandboxUrl(e.target.value)}
          required
          placeholder={`https://widgets.${viewer.sandboxParentDomain}`}
        />
        <small>Must be https and under {viewer.sandboxParentDomain}. Never point this at production.</small>
      </label>

      <label>
        API URLs <span className="muted">(optional)</span>
        <textarea rows={2} value={apiUrls} onChange={(e) => setApiUrls(e.target.value)} />
        <small>
          One per line, under {viewer.sandboxParentDomain}. Add the backend your app calls, such as{' '}
          <code>https://widgets-api.{viewer.sandboxParentDomain}</code>. A single-page app is just static files, so
          without this the agent only tests the shell and never the access control behind it.
        </small>
      </label>

      <label>
        Login URL <span className="muted">(optional)</span>
        <input type="url" value={loginUrl} onChange={(e) => setLoginUrl(e.target.value)} />
        <small>Where the agent should sign in. Defaults to the sandbox URL.</small>
      </label>

      <label>
        Login instructions <span className="muted">(optional)</span>
        <textarea rows={3} value={loginPrompt} onChange={(e) => setLoginPrompt(e.target.value)} maxLength={2000} />
        <small>
          Leave blank for the standard Cognito sign-in instructions. Override it if your app adds steps the
          agent would not guess, naming the fields and buttons to use and how it can tell sign-in worked.
        </small>
      </label>

      <label>
        Reachable but not to be attacked <span className="muted">(optional)</span>
        <textarea rows={3} value={accessibleUrls} onChange={(e) => setAccessibleUrls(e.target.value)} />
        <small>
          One URL pattern per line, for anything the agent must load but not attack, such as a CDN. The Cognito
          sign-in hosts are added automatically, so they do not need listing.
        </small>
      </label>

      <label>
        Out of scope <span className="muted">(optional)</span>
        <textarea rows={3} value={outOfScopeUrls} onChange={(e) => setOutOfScopeUrls(e.target.value)} />
        <small>One URL pattern per line the agent must leave alone, such as an admin or billing path.</small>
      </label>

      <label className="checkbox">
        <input type="checkbox" checked={excludeDos} onChange={(e) => setExcludeDos(e.target.checked)} />
        Skip denial-of-service testing
        <small>Recommended. A shared sandbox environment is rarely a safe place to test availability.</small>
      </label>

      <div className="form-actions">
        <button className="action" type="submit" disabled={busy}>
          {busy ? 'Working…' : existing ? 'Save changes' : 'Onboard app'}
        </button>
        <button className="action secondary" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>

      {!existing ? (
        <p className="muted">
          This creates AWS resources, so it takes a few seconds. Code scanning is already covering every repository in the
          org through the Inspector GitHub App — onboarding here is what adds pen testing.
        </p>
      ) : null}
    </form>
  );
}

function RemoveAppDialog({
  config,
  app,
  onCancel,
  onDone,
}: {
  config: PortalConfig;
  app: OnboardedApp;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function remove() {
    setBusy(true);
    setError(undefined);

    try {
      await api.removeApp(config, app.slug);
      onDone();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not remove the app.');
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true">
      <div className="card dialog">
        <h2>Remove {app.displayName}?</h2>

        <p>
          This permanently deletes both pen test configurations for <code>{app.slug}</code> and removes it from
          Lighthouse. You will need to onboard it again to resume testing.
        </p>
        <p className="muted">
          Findings already reported stay in the Security Agent console, the shared pen test identity is untouched, and
          code scanning is org-wide so it carries on regardless.
        </p>

        {error ? <Notice kind="error">{error}</Notice> : null}

        <label>
          Type <code>{app.slug}</code> to confirm
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        </label>

        <div className="form-actions">
          <button className="action danger" onClick={remove} disabled={busy || typed !== app.slug}>
            {busy ? 'Removing…' : 'Remove app'}
          </button>
          <button className="action secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
